<script module lang="ts">
  export const dailyRulesModal = new ModalQueryState('rules', { layer: 700 });
</script>

<script lang="ts">
  import type { GameMode } from '~/store/game.svelte';
  import ActionButton from '~/components/ActionButton.svelte';
  import Home from '~icons/mdi/home';
  import Close from '~icons/mdi/close';
  import Play from '~icons/mdi/play';
  import Swaps from '../Swaps.svelte';
  import Shuffle from '../Shuffle.svelte';
  import WordRules from './WordRules.svelte';
  import Gauge from '~/gauge/daily/Gauge.svelte';
  import LeftoverTiles from './LeftoverTiles.svelte';
  import GameContext from '~/store/GameContext.svelte';
  import { ModalQueryState } from '~/lib/nav.svelte';
  import { onboarded, tutorialViewed } from '../state.svelte';
  import type { DailyPuzzle, ModalSection } from '~/types';
  import { goto } from '$app/navigation';
  import { page } from '$app/state';
  import Directory from '~/components/Directory.svelte';
  import SlidePanel from '~/components/SlidePanel.svelte';
  import SplashLogo from '~/components/SplashLogo.svelte';
  import HelpCircle from '~/icons/HelpCircle.svelte';
  import School from '~icons/mdi/school'
  import ChevronDownIcon from '~icons/feather/chevron-down';
  import { DailyGame } from '~/store/daily.svelte';
  import { getDailyMode } from '~/lib/queries.svelte';

  const game = new DailyGame({
    variant: getDailyMode(page.params.mode as GameMode),
  } as DailyPuzzle);
  game.swaps = 9;

  const sections: ModalSection[] = [
    {
      id: 'swapping',
      desc: 'Swapping tiles',
    },
    {
      id: 'objective',
      desc: 'Objective of the game',
    },
    {
      id: 'word-rule',
      desc: 'Making words',
    },
    {
      id: 'leftover-tiles',
      desc: 'Leftover tiles',
    },
    {
      id: 'shuffling',
      desc: 'Shuffling the board',
    },
  ];

  const fromHome = $derived(dailyRulesModal.value === 'b');

  const handleToggle = (open: boolean) => {
    if (open) {
      dailyRulesModal.push();
    } else {
      handleClose();
    }
  };

  const handleTutorial = () => {
    onboarded.current = false;
    handleClose();
  }

  const handleBack = async () => {
    await dailyRulesModal.pop();
  };

  const handleClose = async () => {
    if (fromHome) {
      goto('/' + page.params.mode, { shallow: true, replace: true });
    } else {
      await dailyRulesModal.pop();
    }
  };
</script>

<SlidePanel
  open={dailyRulesModal.open}
  scrollOffscreen={tutorialViewed.current || !onboarded.current}
  id="daily-tutorial-modal"
  onToggle={handleToggle}
  maxWidth="32em"
>
  {#snippet title()}
    <SplashLogo animate gameOver />
  {/snippet}
  {#snippet handle()}
    <h1 class="subtitle">
      <HelpCircle />How to play
    </h1>
  {/snippet}
  {#snippet content()}
    <div class="content">
      {#if tutorialViewed.current}
        <Directory sections={sections} />
      {/if}
      <h2 id="swapping">Swap letters by tapping or dragging them</h2>
      <Swaps play={dailyRulesModal.open} />
      <h2 id="objective">Clear the board in under {page.params.mode === 'daily' ? 20 : 18} swaps</h2>
      <p>Each swap drains your gauge. Finish the game before you get to zero.</p>
      <GameContext game={game}><Gauge /></GameContext>

      <div>
        <p>You are awarded stars based on how many swaps you have remaining.</p>
        <!--<ul style='display: flex; flex-direction: column; justify-content: center'>
          <li>10 swaps = 3 stars</li>
          <li>5 swaps = 2 stars</li>
          <li>0 swaps = 1 star</li>
        </ul>-->
      </div>
      <h2 id="making-words">Making words</h2>
      <p>
        Words must span entire row or column. After forming a word, it is
        removed from the board.
      </p>
      <WordRules />
      <h2 id="leftover-tiles">Leftover Tiles</h2>
      <LeftoverTiles />
      <div class="tutorial-block">
        <p>The game ends when the board is cleared or there are no possible words to make</p>

        <p>
          If there are letters left over, their points are deducted from your remaining swaps. 

          {#if page.params.mode === 'daily'}
            Rarer letters are worth more points
          {/if}
        </p>
      </div>
      <!--
      <div class=tutorial-block>
        <Flash size='1.5em' />
        <p class=primary>Create chains by creating multiple words in a swap</p>
        <p class=secondary>Chains give you back one swap per word</p>
      </div>
      -->
      <h2 id="shuffling">Shuffling the board</h2>
      <div class="tutorial-block">
        <p>Use a shuffle to mix up the letters on the board</p>
        <Shuffle play={dailyRulesModal.open} />
        <p>Shuffles do not use up swaps</p>
      </div>
    </div>
  {/snippet}
  {#snippet controls()}
    {#if fromHome}
      <ActionButton onClick={handleBack}>
        <Home />
        Home
      </ActionButton>
      <ActionButton onClick={handleTutorial}>
        <School />
        Tutorial
      </ActionButton>
       <ActionButton onClick={handleClose}>
        Play
        <Play />
      </ActionButton>
    {:else}
      <ActionButton onClick={handleTutorial}>
        <School />
        Tutorial
      </ActionButton>
      <ActionButton onClick={handleBack}>
        {#if tutorialViewed.current}
          <Close />
          Close
        {:else}
          <ChevronDownIcon />
          Hide
        {/if}
      </ActionButton>
    {/if}
  {/snippet}
</SlidePanel>

<style lang="scss">
  * {
    text-align: center;
  }
  .content {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 0 1em;
    & :global(.tile-container),
    :global(.tile) {
      z-index: unset !important;
    }
    :global(h1),
    :global(h2),
    :global(h3),
    :global(h4) {
      width: 100%;
    }
  }
  h2 {
    margin-top: 2em;
    font-size: 1.25rem;
  }
  .subtitle {
    margin: 0;
    margin-left: -2em;
    gap: 1em;
    display: flex;
    align-items: center;
    justify-content: center;
  }
</style>
